<script setup lang="ts">
import { h, type VNodeChild } from 'vue'

import {
  type ContentBodyMark,
  type ContentBodyNode,
  parseContentBody,
} from '../../utils/contentBody'
import { embedSrc, embedTitle } from '../../utils/embed'

import UiPicture from '~/app/components/Image/UiPicture.vue'

// Рендер TipTap-документа блоками витрины (SKUBY-CMS-MODULE-SPEC.md §8.2):
// неизвестные типы нод пропускаются молча — forward-совместимость с нодами Phase 1
const props = defineProps<{
  bodyJson?: string | null
}>()

const blocks = computed(() => parseContentBody(props.bodyJson))

function renderMarks(text: VNodeChild, marks?: ContentBodyMark[]): VNodeChild {
  if (!marks?.length) return text
  return marks.reduce<VNodeChild>((child, mark) => {
    switch (mark.type) {
      case 'bold':
        return h('strong', child)
      case 'italic':
        return h('em', child)
      case 'strike':
        return h('s', child)
      case 'link':
        return h(
          'a',
          {
            href: String(mark.attrs?.href ?? '#'),
            rel: 'noopener',
            target: mark.attrs?.target ?? undefined,
            class: 'text-primary underline underline-offset-2',
          },
          child,
        )
      default:
        return child
    }
  }, text)
}

function renderChildren(nodes?: ContentBodyNode[]): VNodeChild[] {
  return (nodes ?? []).map(renderNode).filter(v => v !== null)
}

function renderNode(node: ContentBodyNode): VNodeChild {
  switch (node.type) {
    case 'text':
      return renderMarks(node.text ?? '', node.marks)
    case 'hardBreak':
      return h('br')
    case 'paragraph':
      return h('p', { class: 'mb-4 leading-relaxed' }, renderChildren(node.content))
    case 'heading': {
      const level = Number(node.attrs?.level ?? 2)
      const tag = level === 3 ? 'h3' : level === 4 ? 'h4' : 'h2'
      const size
        = level === 3 ? 'text-xl' : level === 4 ? 'text-lg' : 'text-2xl'
      return h(tag, { class: `${size} mt-8 mb-4 font-semibold` }, renderChildren(node.content))
    }
    case 'bulletList':
      return h('ul', { class: 'mb-4 list-disc space-y-1 pl-6' }, renderChildren(node.content))
    case 'orderedList':
      return h('ol', { class: 'mb-4 list-decimal space-y-1 pl-6' }, renderChildren(node.content))
    case 'listItem':
      return h('li', renderChildren(node.content))
    case 'blockquote':
      return h(
        'blockquote',
        { class: 'mb-4 border-l-4 border-neutral-200 pl-4 italic' },
        renderChildren(node.content),
      )
    case 'horizontalRule':
      return h('hr', { class: 'my-8 border-neutral-200' })
    case 'table':
      // горизонтальный скролл — на обёртке: широкая таблица не должна
      // растягивать страницу на мобильных
      return h(
        'div',
        { class: 'my-6 overflow-x-auto' },
        h(
          'table',
          { class: 'w-full border-collapse text-sm' },
          h('tbody', renderChildren(node.content)),
        ),
      )
    case 'tableRow':
      return h('tr', renderChildren(node.content))
    case 'tableHeader':
    case 'tableCell': {
      const tag = node.type === 'tableHeader' ? 'th' : 'td'
      const attrs = node.attrs ?? {}
      return h(
        tag,
        {
          colspan: Number(attrs.colspan ?? 1) > 1 ? Number(attrs.colspan) : undefined,
          rowspan: Number(attrs.rowspan ?? 1) > 1 ? Number(attrs.rowspan) : undefined,
          class: [
            'border border-neutral-200 px-3 py-2 align-top',
            node.type === 'tableHeader' ? 'bg-neutral-50 text-left font-semibold' : '',
          ],
        },
        renderChildren(node.content),
      )
    }
    case 'embed': {
      // src собирается по whitelist провайдеров (utils/embed.ts, ADR-0002):
      // произвольный адрес из тела материала в iframe не попадёт
      const src = embedSrc(node.attrs ?? {})
      if (!src) return null
      return h(
        'div',
        { class: 'relative my-6 aspect-video overflow-hidden rounded-xl bg-neutral-900' },
        h('iframe', {
          src,
          title: embedTitle(node.attrs?.provider),
          class: 'absolute inset-0 h-full w-full',
          loading: 'lazy',
          allow: 'encrypted-media; picture-in-picture; fullscreen',
          allowfullscreen: true,
          referrerpolicy: 'strict-origin-when-cross-origin',
        }),
      )
    }
    case 'resourceImage': {
      const resourceId = node.attrs?.resourceId
      if (!resourceId) return null
      return h(
        'figure',
        { class: 'my-6' },
        h(UiPicture, {
          src: String(resourceId),
          alt: String(node.attrs?.alt ?? ''),
          loading: 'lazy',
          sizes: '100vw md:720px',
          class: 'overflow-hidden rounded-xl',
        }),
      )
    }
    default:
      return null
  }
}

const Body = () => h('div', renderChildren(blocks.value))
</script>

<template>
  <Body />
</template>
